import React, { useState } from 'react';
import { Table, ArrowUpDown, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { MonthlyDataRow } from '../../../types/acquisitionAnalysis';

interface MonthlyDataSectionProps {
  data: MonthlyDataRow[];
}

type SortField = 'month' | 'trailIncome' | 'upfrontIncome' | 'clawbacks' | 'netIncome';
type SortDirection = 'asc' | 'desc';

export const MonthlyDataSection: React.FC<MonthlyDataSectionProps> = ({ data }) => {
  const [sortField, setSortField] = useState<SortField>('month');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'month' ? 'desc' : 'desc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    let comparison = 0;
    if (sortField === 'month') {
      comparison = a.month.localeCompare(b.month);
    } else {
      comparison = a[sortField] - b[sortField];
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Calculate totals
  const totals = data.reduce(
    (acc, row) => ({
      trailIncome: acc.trailIncome + row.trailIncome,
      upfrontIncome: acc.upfrontIncome + row.upfrontIncome,
      clawbacks: acc.clawbacks + row.clawbacks,
      netIncome: acc.netIncome + row.netIncome,
    }),
    { trailIncome: 0, upfrontIncome: 0, clawbacks: 0, netIncome: 0 }
  );

  // Calculate growth trend for each row
  const getGrowthIndicator = (currentIndex: number) => {
    if (currentIndex === 0) return null;
    const current = sortedData[currentIndex].trailIncome;
    const previous = sortedData[currentIndex - 1].trailIncome;
    if (previous === 0) return null;

    const growthPct = ((current - previous) / previous) * 100;
    if (growthPct > 3) {
      return <TrendingUp className="w-4 h-4 text-green-400" />;
    } else if (growthPct < -3) {
      return <TrendingDown className="w-4 h-4 text-red-400" />;
    }
    return <Minus className="w-4 h-4 text-slate-500" />;
  };

  const SortableHeader: React.FC<{ field: SortField; label: string }> = ({ field, label }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-2">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${sortField === field ? 'text-indigo-400' : ''}`} />
      </div>
    </th>
  );

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Table className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No monthly data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Table className="w-5 h-5 text-indigo-400" />
          Monthly Commission Data
        </h3>
        <p className="text-sm text-slate-400 mb-4">
          Complete monthly breakdown of trail income, upfront commissions, and clawbacks.
          Click column headers to sort.
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-700">
        <table className="min-w-full divide-y divide-slate-700">
          <thead className="bg-slate-800/50">
            <tr>
              <SortableHeader field="month" label="Month" />
              <SortableHeader field="trailIncome" label="Trail Income" />
              <SortableHeader field="upfrontIncome" label="Upfront" />
              <SortableHeader field="clawbacks" label="Clawbacks" />
              <SortableHeader field="netIncome" label="Net Income" />
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Trend
              </th>
            </tr>
          </thead>
          <tbody className="bg-slate-800/30 divide-y divide-slate-700/50">
            {sortedData.map((row, index) => (
              <tr key={row.month} className="hover:bg-slate-700/30 transition-colors">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white">
                  {row.monthLabel}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-green-300">
                  {formatCurrency(row.trailIncome)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-300">
                  {formatCurrency(row.upfrontIncome)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-red-300">
                  {row.clawbacks > 0 ? `-${formatCurrency(row.clawbacks)}` : '$0'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white">
                  {formatCurrency(row.netIncome)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {sortDirection === 'desc' && sortField === 'month'
                    ? getGrowthIndicator(sortedData.length - 1 - index)
                    : getGrowthIndicator(index)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-800/80 border-t-2 border-slate-600">
            <tr>
              <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-white">
                Total ({data.length} months)
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-green-300">
                {formatCurrency(totals.trailIncome)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-blue-300">
                {formatCurrency(totals.upfrontIncome)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-red-300">
                {totals.clawbacks > 0 ? `-${formatCurrency(totals.clawbacks)}` : '$0'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-white">
                {formatCurrency(totals.netIncome)}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
          <p className="text-sm text-slate-400 mb-1">Total Trail Income</p>
          <p className="text-xl font-bold text-green-300">{formatCurrency(totals.trailIncome)}</p>
        </div>
        <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <p className="text-sm text-slate-400 mb-1">Total Upfront</p>
          <p className="text-xl font-bold text-blue-300">{formatCurrency(totals.upfrontIncome)}</p>
        </div>
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-slate-400 mb-1">Total Clawbacks</p>
          <p className="text-xl font-bold text-red-300">
            {totals.clawbacks > 0 ? `-${formatCurrency(totals.clawbacks)}` : '$0'}
          </p>
        </div>
        <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
          <p className="text-sm text-slate-400 mb-1">Net Total</p>
          <p className="text-xl font-bold text-indigo-300">{formatCurrency(totals.netIncome)}</p>
        </div>
      </div>

      <div className="p-4 bg-slate-700/30 border border-slate-600 rounded-lg">
        <p className="text-sm text-slate-400">
          <strong className="text-white">Note:</strong> This table shows aggregated monthly data
          across all brokers. Trail income represents ongoing trailing commissions. Upfront
          represents settlement/new loan commissions. Clawbacks are commissions returned due to
          early loan discharge.
        </p>
      </div>
    </div>
  );
};
